/**
 * Custom Variant Picker with Skio Integration
 * Handles subscription pricing and variant selection
 */

class CustomVariantPicker {
  constructor() {
    this.product = null;
    this.selectedVariant = null;
    this.selectedSellingPlan = null;
    this.skioSubscriptionPrice = null;
    this.skioSubscriptionDiscount = null;

    // Cache to store correct Skio pricing for each variant
    this.variantPriceCache = {};

    // Flag to track variant transitions and prevent stale price flashes
    this.isTransitioning = false;

    // Flag to allow initial price update on page load
    this.hasRunInitialUpdate = false;

    // Debounce timer for price updates
    this.updatePricesTimer = null;

    // Debounce timer for height matching
    this.matchHeightsTimer = null;

    // ReCharge purchase mode synchronization state
    this.currentPurchaseMode = null;
    this.rechargeSyncInterval = null;
    this.rechargeMutationObserver = null;
    this.rechargeWidgetObserver = null;
    this.rechargeWidgetCheckInterval = null;
    this.rechargeWidgetNode = null;
    this.rechargeEventHandler = null;
    this.rechargeHeightSyncTimer = null;
    this.rechargeModeResyncTimer = null;
    this.rechargeModeRetryAttempts = 0;
    this.rechargeMaxModeRetryAttempts = 10;
    this.rechargeModeLockUntil = 0;
    this.rechargeLockedMode = null;
    this.debugEnabled = Boolean(window.NEBULA_VARIANT_PICKER_DEBUG);

    // Get the correct Shopify money format
    // Try multiple sources - Shopify provides money_format in different places
    this.moneyFormat = window.Shopify?.money_format || window.theme?.moneyFormat || '${{amount}}';

    this.debug('💰 Initialized with money format:', this.moneyFormat);

    this.init();
  }

  debug(...args) {
    if (!this.debugEnabled) return;

    console.log(...args);
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Get product data
    this.loadProductData();

    // Listen for variant changes
    const variantSelects = document.querySelector('variant-selects');
    if (variantSelects) {
      variantSelects.addEventListener('change', () => {
        this.handleVariantChange();
      });
    }

    // Listen for variant input changes directly
    const variantInputs = document.querySelectorAll('.variant-box-input');
    variantInputs.forEach((input) => {
      input.addEventListener('change', () => {
        this.debug('🔄 Variant box clicked - prices already correct from Liquid, no update needed');

        const modeBeforeChange = this.getFallbackPurchaseMode();
        this.lockRechargeMode(modeBeforeChange);
        this.applyPurchaseMode(modeBeforeChange, { force: true });

        // Only update the variant reference, don't touch prices
        const variantInput = document.querySelector('input[name="id"]');
        if (variantInput && variantInput.value) {
          this.updateVariant(variantInput.value);
        }

        this.scheduleRechargeModeResync();

        // Don't call updateAllPrices() - Liquid already has correct prices!
      });
    });

    // Set default variant to 32 Pods if no variant is in URL
    this.setDefaultVariant();

    // Initial update based on URL params
    this.updateFromURL();

    this.setupRechargePurchaseModeSync();

    // Re-run mode sync when navigating back/forward.
    window.addEventListener('pageshow', () => {
      this.setupRechargePurchaseModeSync();
    });

    // Match all heights on load and resize (debounced)
    // Wait a bit for elements to render and prices to load
    setTimeout(() => {
      this.matchAllHeights();
    }, 500);

    // Add resize listener with debouncing
    window.addEventListener('resize', () => {
      this.debouncedMatchHeights();
    });

    // Also match heights after variant changes (with slight delay for DOM updates)
    variantInputs.forEach((input) => {
      input.addEventListener('change', () => {
        setTimeout(() => {
          this.matchAllHeights();
        }, 100);
      });
    });
  }

  setupRechargePurchaseModeSync() {
    const variantBoxesContainers = document.querySelectorAll('.variant-boxes');
    if (!variantBoxesContainers.length) return;

    const skioTitle = document.querySelector('.skio-plan-picker-title');
    if (skioTitle) {
      skioTitle.style.display = 'none';
    }

    const rechargeWidget = document.querySelector('recharge-subscription-widget');

    if (!rechargeWidget) {
      document.body.classList.add('no-subscriptions');
      this.teardownRechargePurchaseModeBindings();
      this.applyPurchaseMode(this.getFallbackPurchaseMode(), { force: true });
      this.watchRechargeWidgetReplacement();
      return;
    }

    document.body.classList.remove('no-subscriptions');
    this.bindRechargePurchaseModeListeners(rechargeWidget);
    this.syncRechargePurchaseMode(rechargeWidget, { force: true });
    this.watchRechargeWidgetReplacement();
  }

  normalizePurchaseMode(value) {
    const normalizedValue = String(value || '').toLowerCase();

    if (normalizedValue.includes('one')) return 'onetime';
    if (normalizedValue.includes('sub')) return 'subscription';

    return 'subscription';
  }

  getFallbackPurchaseMode() {
    if (this.currentPurchaseMode) {
      return this.currentPurchaseMode;
    }

    if (document.body.classList.contains('recharge-onetime-mode')) {
      return 'onetime';
    }

    const variantBoxesContainers = document.querySelectorAll('.variant-boxes');
    const hasOneTimeClass = Array.from(variantBoxesContainers).some((container) =>
      container.classList.contains('show-onetime'),
    );

    if (hasOneTimeClass) {
      return 'onetime';
    }

    return 'subscription';
  }

  isRechargeModeReadable(widget) {
    if (!widget || !widget.shadowRoot) return false;

    const purchaseOptionInputs = widget.shadowRoot.querySelectorAll('input[name="purchaseOption"]');
    if (purchaseOptionInputs.length > 0) return true;

    const selectedOption = widget.shadowRoot.querySelector('.rc-purchase-option[rc-selected], [rc-selected]');
    return !!selectedOption;
  }

  getRechargePurchaseMode(widget) {
    if (!widget || !widget.shadowRoot) {
      return this.getFallbackPurchaseMode();
    }

    const checkedInput = widget.shadowRoot.querySelector('input[name="purchaseOption"]:checked');
    if (checkedInput && checkedInput.value) {
      return this.normalizePurchaseMode(checkedInput.value);
    }

    const selectedOption = widget.shadowRoot.querySelector('.rc-purchase-option[rc-selected], [rc-selected]');
    if (selectedOption) {
      const selectedClasses = selectedOption.className || '';
      const selectedPart = selectedOption.getAttribute('part') || '';

      if (selectedClasses.includes('onetime') || selectedPart.includes('onetime')) {
        return 'onetime';
      }

      if (selectedClasses.includes('subscription') || selectedPart.includes('subscription')) {
        return 'subscription';
      }
    }

    return this.getFallbackPurchaseMode();
  }

  applyPurchaseMode(mode, { force = false } = {}) {
    const normalizedMode = this.normalizePurchaseMode(mode);
    const variantBoxesContainers = document.querySelectorAll('.variant-boxes');
    const shouldShowOneTime = normalizedMode === 'onetime';
    const hasBodyOneTimeClass = document.body.classList.contains('recharge-onetime-mode');

    if (shouldShowOneTime !== hasBodyOneTimeClass) {
      document.body.classList.toggle('recharge-onetime-mode', shouldShowOneTime);
    }

    const isModeAppliedInDom = variantBoxesContainers.length
      ? Array.from(variantBoxesContainers).every((variantBoxesContainer) => {
          const hasOneTimeClass = variantBoxesContainer.classList.contains('show-onetime');
          return shouldShowOneTime ? hasOneTimeClass : !hasOneTimeClass;
        })
      : true;

    if (
      !force &&
      this.currentPurchaseMode === normalizedMode &&
      isModeAppliedInDom &&
      shouldShowOneTime === hasBodyOneTimeClass
    )
      return;

    this.currentPurchaseMode = normalizedMode;

    variantBoxesContainers.forEach((variantBoxesContainer) => {
      if (shouldShowOneTime) {
        variantBoxesContainer.classList.add('show-onetime');
      } else {
        variantBoxesContainer.classList.remove('show-onetime');
      }
    });

    clearTimeout(this.rechargeHeightSyncTimer);
    this.rechargeHeightSyncTimer = setTimeout(() => {
      this.matchAllHeights();
    }, 100);
  }

  syncRechargePurchaseMode(widget, { force = false } = {}) {
    if (this.isRechargeModeLocked()) {
      this.applyPurchaseMode(this.rechargeLockedMode || this.getFallbackPurchaseMode(), { force: true });
      this.scheduleRechargeModeResync(75);
      return;
    }

    if (!this.isRechargeModeReadable(widget)) {
      this.applyPurchaseMode(this.getFallbackPurchaseMode(), { force: true });
      this.scheduleRechargeModeResync(75);
      return;
    }

    this.rechargeModeRetryAttempts = 0;

    const purchaseMode = this.getRechargePurchaseMode(widget);
    this.applyPurchaseMode(purchaseMode, { force });
  }

  scheduleRechargeModeResync(delay = 200) {
    clearTimeout(this.rechargeModeResyncTimer);

    this.rechargeModeResyncTimer = setTimeout(() => {
      const widget = document.querySelector('recharge-subscription-widget');
      if (!widget) return;

      if (this.isRechargeModeLocked()) {
        this.scheduleRechargeModeResync(75);
        return;
      }

      if (!this.isRechargeModeReadable(widget)) {
        if (this.rechargeModeRetryAttempts < this.rechargeMaxModeRetryAttempts) {
          this.rechargeModeRetryAttempts += 1;
          this.scheduleRechargeModeResync(75);
        }
        return;
      }

      this.rechargeModeRetryAttempts = 0;
      this.syncRechargePurchaseMode(widget, { force: true });
    }, delay);
  }

  teardownRechargePurchaseModeBindings() {
    if (this.rechargeMutationObserver) {
      this.rechargeMutationObserver.disconnect();
      this.rechargeMutationObserver = null;
    }

    if (this.rechargeSyncInterval) {
      clearInterval(this.rechargeSyncInterval);
      this.rechargeSyncInterval = null;
    }

    if (this.rechargeWidgetNode && this.rechargeEventHandler) {
      this.rechargeWidgetNode.removeEventListener('change', this.rechargeEventHandler, true);
      this.rechargeWidgetNode.removeEventListener('input', this.rechargeEventHandler, true);
    }

    clearTimeout(this.rechargeModeResyncTimer);
    this.rechargeModeResyncTimer = null;
    this.rechargeModeRetryAttempts = 0;
    this.rechargeModeLockUntil = 0;
    this.rechargeLockedMode = null;

    this.rechargeEventHandler = null;
    this.rechargeWidgetNode = null;
  }

  lockRechargeMode(mode, duration = 350) {
    this.rechargeLockedMode = this.normalizePurchaseMode(mode);
    this.rechargeModeLockUntil = Date.now() + duration;
  }

  isRechargeModeLocked() {
    return Date.now() < this.rechargeModeLockUntil;
  }

  bindRechargePurchaseModeListeners(widget) {
    this.teardownRechargePurchaseModeBindings();

    this.rechargeWidgetNode = widget;
    this.rechargeEventHandler = () => {
      this.syncRechargePurchaseMode(widget);
    };

    widget.addEventListener('change', this.rechargeEventHandler, true);
    widget.addEventListener('input', this.rechargeEventHandler, true);

    if (widget.shadowRoot) {
      this.rechargeMutationObserver = new MutationObserver(() => {
        this.syncRechargePurchaseMode(widget);
      });

      this.rechargeMutationObserver.observe(widget.shadowRoot, {
        attributes: true,
        attributeFilter: ['checked', 'rc-selected', 'class', 'value'],
        childList: true,
        subtree: true,
      });
    }

    this.rechargeSyncInterval = setInterval(() => {
      this.syncRechargePurchaseMode(widget);
    }, 300);
  }

  watchRechargeWidgetReplacement() {
    if (this.rechargeWidgetObserver || this.rechargeWidgetCheckInterval) return;

    const syncWidget = () => {
      const currentWidget = document.querySelector('recharge-subscription-widget');

      if (currentWidget === this.rechargeWidgetNode) return;

      if (!currentWidget) {
        this.teardownRechargePurchaseModeBindings();
        document.body.classList.add('no-subscriptions');
        this.applyPurchaseMode(this.getFallbackPurchaseMode(), { force: true });
        return;
      }

      document.body.classList.remove('no-subscriptions');
      this.bindRechargePurchaseModeListeners(currentWidget);
      this.syncRechargePurchaseMode(currentWidget, { force: true });
    };

    this.rechargeWidgetObserver = new MutationObserver(() => {
      syncWidget();
    });

    this.rechargeWidgetObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    this.rechargeWidgetCheckInterval = setInterval(() => {
      syncWidget();
    }, 400);

    syncWidget();
  }

  queueRechargeInitializationSync({ variantInput = null, variantId = null } = {}) {
    // Recharge widget can render asynchronously after initial variant selection.
    // Retry sync and re-fire variant change a few times so the initial programmatic
    // selection behaves like a manual change after all listeners are ready.
    const syncDelays = [75, 200, 500, 1000];

    for (const delay of syncDelays) {
      setTimeout(() => {
        const rechargeWidget = document.querySelector('recharge-subscription-widget');
        const mainVariantInput = document.querySelector('input[name="id"]');

        if (!rechargeWidget && variantInput && mainVariantInput && variantId) {
          mainVariantInput.value = variantId;
          mainVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
          variantInput.dispatchEvent(new Event('change', { bubbles: true }));
          this.handleVariantChange();
        }

        this.setupRechargePurchaseModeSync();
        this.scheduleRechargeModeResync(75);
      }, delay);
    }
  }

  setDefaultVariant() {
    // Only set default if there's no variant in the URL
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('variant')) {
      this.debug('Variant already in URL, skipping default selection');
      return;
    }

    // Find all variant inputs (Pods, Packets, Count, Bags, and Pack)
    const variantInputs = document.querySelectorAll('.variant-box-input');
    const mainVariantInput = document.querySelector('input[name="id"]');

    if (!variantInputs.length || !mainVariantInput) {
      this.debug('⚠️ No variant inputs found');
      return;
    }

    // Find the first available (non-disabled) variant
    let firstAvailableVariant = null;
    for (const variant of variantInputs) {
      if (!variant.disabled && !variant.hasAttribute('disabled')) {
        firstAvailableVariant = variant;
        break;
      }
    }

    if (!firstAvailableVariant) {
      this.debug('⚠️ No available variants found (all sold out)');
      return;
    }

    const unitType = firstAvailableVariant.dataset.unitType;
    const unitCount = firstAvailableVariant.dataset.unitCount;
    this.debug(`🎯 First available variant found: ${unitCount} ${unitType}`);

    if (firstAvailableVariant && mainVariantInput) {
      this.debug(`🎯 Setting first available variant: ${unitCount} ${unitType}`);

      firstAvailableVariant.checked = true;
      firstAvailableVariant.dispatchEvent(new Event('change', { bubbles: true }));

      let variantId = firstAvailableVariant.dataset.variantId;
      if (!variantId) {
        const productUrl = firstAvailableVariant.dataset.productUrl;
        if (productUrl) {
          const urlMatch = productUrl.match(/[?&]variant=(\d+)/);
          if (urlMatch) variantId = urlMatch[1];
        }
      }

      if (variantId) {
        mainVariantInput.value = variantId;
        mainVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
        this.handleVariantChange();
        this.queueRechargeInitializationSync({ variantInput: firstAvailableVariant, variantId });
        this.debug(`✅ First available variant set: ${unitCount} ${unitType}`);
      }
    }
  }

  monitorSkioInput() {
    let lastSellingPlanValue = null;
    let currentDisplayMode = 'subscription'; // Track current display mode

    // Poll for Skio component and watch for changes
    const checkSkioInput = () => {
      const skioPlanPicker = document.querySelector('skio-plan-picker');
      if (skioPlanPicker) {
        const sellingPlanInput = skioPlanPicker.querySelector('input[name="selling_plan"]');
        if (sellingPlanInput && !sellingPlanInput.dataset.monitored) {
          this.debug('👀 Setting up Skio input monitoring');
          sellingPlanInput.dataset.monitored = 'true';
          lastSellingPlanValue = sellingPlanInput.value;

          // Debounced function to toggle display mode (subscription vs one-time)
          const debouncedUpdate = () => {
            clearTimeout(this.updatePricesTimer);
            this.updatePricesTimer = setTimeout(() => {
              // Check selling plan input value (empty = one-time, has value = subscription)
              const isOneTime = !sellingPlanInput.value;
              const newMode = isOneTime ? 'onetime' : 'subscription';

              // Only toggle if mode actually changed
              if (newMode !== currentDisplayMode) {
                currentDisplayMode = newMode;

                // Find variant boxes container
                const variantBoxesContainer = document.querySelector('.variant-boxes');
                if (variantBoxesContainer) {
                  if (isOneTime) {
                    // Show one-time prices, hide subscription prices
                    variantBoxesContainer.classList.add('show-onetime');
                    this.debug('✅ Switched to one-time mode');
                  } else {
                    // Show subscription prices, hide one-time prices
                    variantBoxesContainer.classList.remove('show-onetime');
                    this.debug('✅ Switched to subscription mode');
                  }
                }
              } else {
                this.debug(`⏭️ Display mode unchanged (${currentDisplayMode}), skipping toggle`);

                // Defensive: ensure the correct class is still applied
                const variantBoxesContainer = document.querySelector('.variant-boxes');
                if (variantBoxesContainer) {
                  const hasClass = variantBoxesContainer.classList.contains('show-onetime');
                  this.debug(
                    `🔍 Class check: show-onetime = ${hasClass}, should be ${currentDisplayMode === 'onetime'}`,
                  );

                  // If class state doesn't match current mode, fix it
                  if (currentDisplayMode === 'onetime' && !hasClass) {
                    this.debug('⚠️ Class mismatch! Re-adding show-onetime');
                    variantBoxesContainer.classList.add('show-onetime');
                  } else if (currentDisplayMode === 'subscription' && hasClass) {
                    this.debug('⚠️ Class mismatch! Removing show-onetime');
                    variantBoxesContainer.classList.remove('show-onetime');
                  }
                }
              }
            }, 100);
          };

          // Watch for changes to the input value
          sellingPlanInput.addEventListener('change', () => {
            this.debug('💡 Skio input changed (event):', sellingPlanInput.value);

            // Immediate check to prevent class removal during variant switches
            const isOneTime = !sellingPlanInput.value;
            if (isOneTime === (currentDisplayMode === 'onetime')) {
              // Mode hasn't changed, ensure class is still correct
              const variantBoxesContainer = document.querySelector('.variant-boxes');
              if (variantBoxesContainer && currentDisplayMode === 'onetime') {
                variantBoxesContainer.classList.add('show-onetime');
              }
            }

            debouncedUpdate();
          });

          // Use MutationObserver to catch attribute changes
          const observer = new MutationObserver(() => {
            this.debug('🔄 Skio input mutated (attribute)');

            // Immediate check to prevent class removal during variant switches
            const isOneTime = !sellingPlanInput.value;
            if (isOneTime === (currentDisplayMode === 'onetime')) {
              // Mode hasn't changed, ensure class is still correct
              const variantBoxesContainer = document.querySelector('.variant-boxes');
              if (variantBoxesContainer && currentDisplayMode === 'onetime') {
                variantBoxesContainer.classList.add('show-onetime');
              }
            }

            debouncedUpdate();
          });

          observer.observe(sellingPlanInput, {
            attributes: true,
            attributeFilter: ['value'],
          });

          // Poll the input value directly (catches property changes that don't trigger events)
          setInterval(() => {
            const currentValue = sellingPlanInput.value;
            if (currentValue !== lastSellingPlanValue) {
              this.debug('🔄 Subscription changed:', currentValue ? 'Active' : 'One-time');
              lastSellingPlanValue = currentValue;

              // Immediate check to prevent class removal during variant switches
              const isOneTime = !sellingPlanInput.value;
              if (isOneTime === (currentDisplayMode === 'onetime')) {
                // Mode hasn't changed, ensure class is still correct
                const variantBoxesContainer = document.querySelector('.variant-boxes');
                if (variantBoxesContainer && currentDisplayMode === 'onetime') {
                  variantBoxesContainer.classList.add('show-onetime');
                }
              }

              debouncedUpdate();
            }
          }, 200);
        }
      } else {
        // Skio not loaded yet, check again
        setTimeout(checkSkioInput, 200);
      }
    };

    checkSkioInput();
  }

  loadProductData() {
    const productInfoElement = document.querySelector('[data-product-id]');
    if (productInfoElement) {
      const productId = productInfoElement.dataset.productId;

      // Try to get product data from window
      if (window.ShopifyAnalytics?.meta?.product) {
        this.product = window.ShopifyAnalytics.meta.product;
      }

      // Get current variant from form
      const variantInput = document.querySelector('input[name="id"]');
      if (variantInput && variantInput.value) {
        this.updateVariant(variantInput.value);
      }
    }
  }

  updateFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const sellingPlanId = urlParams.get('selling_plan');

    if (sellingPlanId) {
      // Subscription is pre-selected
      this.selectedSellingPlan = { id: sellingPlanId };
    }

    const variantId = urlParams.get('variant');
    if (variantId) {
      this.updateVariant(variantId);
    }
  }

  handleSkioUpdate(detail) {
    this.debug('🎯 Skio update received:', {
      sellingPlan: detail.sellingPlan?.name,
      subscriptionPrice: detail.subscriptionPrice,
      discount: Math.round(detail.subscriptionDiscount) + '%',
      variant: detail.variant ? `${detail.variant.id} - ${detail.variant.title}` : 'none',
    });

    // Track if this is a subscription/one-time purchase change
    const wasSubscription = !!this.selectedSellingPlan;
    const isSubscription = !!detail.sellingPlan;
    const subscriptionStateChanged = wasSubscription !== isSubscription;

    if (detail.sellingPlan) {
      this.selectedSellingPlan = detail.sellingPlan;
      // Store the actual subscription pricing from Skio (round the discount)
      this.skioSubscriptionPrice = detail.subscriptionPrice;
      this.skioSubscriptionDiscount = Math.round(detail.subscriptionDiscount);

      // Clear transition flag when Skio updates (variant change is complete)
      this.isTransitioning = false;

      // Cache this pricing for the current variant so it persists when switching
      // We need to cache using multiple keys since variant tracking is complex
      if (detail.variant && detail.variant.id) {
        const cacheEntry = {
          subscriptionPrice: detail.subscriptionPrice,
          subscriptionDiscount: Math.round(detail.subscriptionDiscount),
        };

        // Cache with the variant ID from Skio
        this.variantPriceCache[detail.variant.id] = cacheEntry;
        this.variantPriceCache[String(detail.variant.id)] = cacheEntry;

        // Also cache by variant title if available (e.g., "16 Pods")
        if (detail.variant.title) {
          this.variantPriceCache[detail.variant.title] = cacheEntry;
        }

        // Extract pods count from title and cache by that too
        if (detail.variant.title) {
          const podsMatch = detail.variant.title.match(/(\d+)\s*Pods?/i);
          if (podsMatch) {
            this.variantPriceCache[`pods_${podsMatch[1]}`] = cacheEntry;
            this.debug(
              `💾 Cached ${podsMatch[1]} pods: $${(cacheEntry.subscriptionPrice / 100).toFixed(2)}, ${
                cacheEntry.subscriptionDiscount
              }%`,
            );
          }
        }

        this.debug('📦 Cache state:', Object.keys(this.variantPriceCache));
      } else {
        console.warn('⚠️ No variant info in Skio event, cannot cache');
      }
    } else {
      // One-time purchase selected
      this.selectedSellingPlan = null;
      this.skioSubscriptionPrice = null;
      this.skioSubscriptionDiscount = null;
      // Clear cache when one-time is selected
      this.variantPriceCache = {};
    }

    if (detail.variant) {
      this.selectedVariant = detail.variant;
    }

    // Only update prices when switching between subscription/one-time purchase
    // Don't update when just switching variants (Liquid prices are already correct)
    if (subscriptionStateChanged) {
      this.debug('🔄 Subscription state changed, updating all prices');
      this.updateAllPrices();
    } else {
      this.debug('✓ Variant changed but subscription state same - keeping existing prices');
    }
  }

  handleVariantChange() {
    const variantInput = document.querySelector('input[name="id"]');
    if (variantInput && variantInput.value) {
      this.debug('🔄 Variant changed via form - not updating prices (Liquid has correct values)');

      const modeBeforeChange = this.getFallbackPurchaseMode();
      this.lockRechargeMode(modeBeforeChange);
      this.applyPurchaseMode(modeBeforeChange, { force: true });

      // Only update the variant reference, don't touch prices
      this.updateVariant(variantInput.value);
      this.scheduleRechargeModeResync();

      // Don't call updateAllPrices() - prices are correct from Liquid
      // Only subscription/one-time switching should update prices (via handleSkioUpdate)
      // No-subscription products are handled by .no-subscriptions class on body
    }
  }

  updateVariant(variantId) {
    // Find variant in product data or fetch from selected input
    const variantSelector = document.querySelector(`input[data-product-url][value="${variantId}"]`);
    if (variantSelector) {
      // Extract data from the DOM
      const price = this.extractPriceFromVariant(variantId);
      const compareAtPrice = this.extractCompareAtPriceFromVariant(variantId);

      this.selectedVariant = {
        id: variantId,
        price: price,
        compare_at_price: compareAtPrice,
      };
    }
  }

  extractPriceFromVariant(variantId) {
    // Try to get price from variant data attributes or meta
    const priceElement = document.querySelector(`[data-variant-id="${variantId}"]`);
    if (priceElement && priceElement.dataset.price) {
      return parseInt(priceElement.dataset.price);
    }

    // Fallback: get from selected variant's price display
    const selectedVariantData = document.querySelector('[data-selected-variant]');
    if (selectedVariantData) {
      try {
        const variantData = JSON.parse(selectedVariantData.textContent);
        if (variantData.id == variantId) {
          return variantData.price;
        }
      } catch (e) {
        console.error('Error parsing variant data:', e);
      }
    }

    return 0;
  }

  extractCompareAtPriceFromVariant(variantId) {
    const selectedVariantData = document.querySelector('[data-selected-variant]');
    if (selectedVariantData) {
      try {
        const variantData = JSON.parse(selectedVariantData.textContent);
        if (variantData.id == variantId) {
          return variantData.compare_at_price || variantData.price;
        }
      } catch (e) {
        console.error('Error parsing variant data:', e);
      }
    }

    return 0;
  }

  updateAllPrices() {
    /**
     * This method is now deprecated - we use CSS toggle instead
     * Prices are pre-rendered in Liquid and we only toggle visibility via CSS classes
     */
    this.debug('⏭️ updateAllPrices called but skipped - using CSS toggle instead');
  }

  matchPriceBoxHeights() {
    // Only run on screens 1024px or wider
    if (window.innerWidth < 1024) {
      // Reset heights on smaller screens
      const priceBoxes = document.querySelectorAll('.variant-box-price-per-box');
      priceBoxes.forEach((box) => {
        box.style.height = '';
      });
      return;
    }

    // Get all visible price boxes
    const priceBoxes = Array.from(document.querySelectorAll('.variant-box-price-per-box')).filter((box) => {
      const computedStyle = window.getComputedStyle(box);
      return computedStyle.display !== 'none' && computedStyle.visibility !== 'hidden';
    });

    if (priceBoxes.length === 0) return;

    // Reset heights first to get natural heights
    priceBoxes.forEach((box) => {
      box.style.height = '';
    });

    // Get the tallest height
    const heights = priceBoxes.map((box) => box.offsetHeight);
    const maxHeight = Math.max(...heights);

    // Set all boxes to the max height
    priceBoxes.forEach((box) => {
      box.style.height = `${maxHeight}px`;
    });

    this.debug(`📏 Desktop: Matched ${priceBoxes.length} price box heights to ${maxHeight}px`);
  }

  matchMobileHeights() {
    // Only run on screens below 1024px
    if (window.innerWidth >= 1024) {
      // Reset heights on larger screens
      const selectors = ['.variant-box-pods', '.variant-box-price-per-box', '.variant-box-free-shipping'];
      selectors.forEach((selector) => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((el) => {
          el.style.height = '';
        });
      });
      return;
    }

    // Helper function to match heights for a specific selector
    const matchHeightsForSelector = (selector) => {
      const elements = Array.from(document.querySelectorAll(selector)).filter((el) => {
        const computedStyle = window.getComputedStyle(el);
        return computedStyle.display !== 'none' && computedStyle.visibility !== 'hidden';
      });

      if (elements.length === 0) return;

      // Reset heights first to get natural heights
      elements.forEach((el) => {
        el.style.height = '';
      });

      // Get the tallest height
      const heights = elements.map((el) => el.offsetHeight);
      const maxHeight = Math.max(...heights);

      // Set all elements to the max height
      elements.forEach((el) => {
        el.style.height = `${maxHeight}px`;
      });

      this.debug(`📱 Mobile: Matched ${elements.length} ${selector} heights to ${maxHeight}px`);
    };

    // Match heights for each element type
    matchHeightsForSelector('.variant-box-pods');
    matchHeightsForSelector('.variant-box-price-per-box');
    matchHeightsForSelector('.variant-box-free-shipping');
  }

  checkVariantBoxCount() {
    const variantBoxesContainer = document.querySelector('.variant-boxes');
    if (!variantBoxesContainer) return;

    // Count all variant box wrappers
    const variantBoxWrappers = document.querySelectorAll('.variant-box-wrapper');
    const count = variantBoxWrappers.length;

    // Add class if 4 or more variants exist
    if (count >= 4) {
      variantBoxesContainer.classList.add('has-many-variants');
      this.debug(`📦 Added .has-many-variants class (${count} variants)`);
    } else {
      variantBoxesContainer.classList.remove('has-many-variants');
      this.debug(`📦 Removed .has-many-variants class (${count} variants)`);
    }
  }

  debouncedMatchHeights() {
    clearTimeout(this.matchHeightsTimer);
    this.matchHeightsTimer = setTimeout(() => {
      this.matchPriceBoxHeights();
      this.matchMobileHeights();
      this.checkVariantBoxCount();
    }, 150);
  }

  matchAllHeights() {
    this.matchPriceBoxHeights();
    this.matchMobileHeights();
    this.checkVariantBoxCount();
  }

  formatMoney(cents) {
    const placeholderRegex = /{{\s*(\w+)\s*}}/;

    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      if (isNaN(number) || number == null) {
        return '0';
      }

      const numString = (number / 100.0).toFixed(precision);
      const parts = numString.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsStr = parts[1] ? decimal + parts[1] : '';
      return dollars + centsStr;
    }

    const result = this.moneyFormat.replace(placeholderRegex, (match, placeholder) => {
      switch (placeholder) {
        case 'amount':
          return formatWithDelimiters(cents, 2);
        case 'amount_no_decimals':
          return formatWithDelimiters(cents, 0);
        case 'amount_with_comma_separator':
          return formatWithDelimiters(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return formatWithDelimiters(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return formatWithDelimiters(cents, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator':
          return formatWithDelimiters(cents, 0, ' ');
        case 'amount_with_space_separator':
          return formatWithDelimiters(cents, 2, ' ', ',');
        case 'amount_with_period_and_space_separator':
          return formatWithDelimiters(cents, 2, ' ', '.');
        default:
          return match;
      }
    });

    return result;
  }
}

// Initialize when script loads
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CustomVariantPicker();
  });
} else {
  new CustomVariantPicker();
}
